/**
 * Why V2 was created:
 * 
 * The original version of the DateTimePicker did not support editing the value with keyboard.
 * MUI 7 provides a DateTimePicker with keyboard editing and configurable validation straight out of the box.
 * In order to support incremental upgrade of the existing DateTimePicker components in the codebase, a separate version V2 was created alongside V1.
 * 
 * The V1 DateTimePicker was a custom component that provided a 2-step datetime selection process, making use of MUI's DatePicker and TimePicker components.
 * This was originally done to get access to the time picker with a digital clock, but in MUI 7 the DateTimePicker component already uses the digital clock by default,
 * so there is no reason not to use MUI 7 DateTimePicker.
 */

import React, { useCallback, useState } from 'react'
import { isValid } from 'date-fns'
import {
  DateTimePicker as MuiDateTimePicker,
  PickersDay,
  PickersDayProps,
  LocalizationProvider,
  DateTimeValidationError,
} from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { dateFnsLocale, localeDateFormat } from '../../../utils/localeUtils'
import { formatDateTimeWithLocale } from '../../../utils/date-utils'

interface DateTimePickerProps {
  id: string
  label: string
  value: Date | null
  onChange: (selectedDate: Date | null, keyboardInputValue?: string) => void
  onError?: (error: DateTimeValidationError | null) => void
  required?: boolean
  helperText?: string
  isHTL?: boolean
  maxDateTime?: Date | undefined
  minDateTime?: Date | undefined
  disabledDates?: string[]
  inputFormat?: string
  dataTestId?: string
  disabled?: boolean
  minutesStep?: number
}

// Styling constants
const DAY_STYLES = {
  default: {
    '&.MuiPickersDay-today.MuiButtonBase-root:not(.Mui-selected)': {
      border: '1px solid rgba(32,197,160,0.3)',
      backgroundColor: 'rgba(32,197,160,0.3)',
    },
  },
  disabled: {
    '&.MuiPickersDay-root': {
      color: 'rgba(0,0,0,0.2)',
    },
  },
}

const INVALID_DATE_ERROR_MESSAGE = 'Your date is not valid'

function DateTimePicker({
  id,
  label,
  value,
  onChange,
  onError,
  required,
  helperText,
  maxDateTime,
  minDateTime,
  disabledDates = [],
  inputFormat = `${localeDateFormat} HH:mm`,
  dataTestId,
  disabled,
  minutesStep,
}: DateTimePickerProps) {
  const [touched, setTouched] = useState(false)
  const [internalError, setInternalError] = useState<DateTimeValidationError | null>(null)

  const disableDate = useCallback(
    (date: Date) =>
      disabledDates?.some(
        disabledDate => date.toISOString() === new Date(disabledDate).toISOString()
      ) ?? false,
    [disabledDates]
  )

  const handleChange = (selectedDate: Date | null) => {
    setTouched(true)
    onChange(selectedDate)
  }

  const handleError = (newError: DateTimeValidationError | null) => {
    setInternalError(newError)
    onError?.(newError)
  }

  const computedHelperText = React.useMemo(() => {
    switch (internalError) {
      case 'maxDate':
      case 'maxTime':
        return maxDateTime ? `Please select a date before ${formatDateTimeWithLocale(maxDateTime)}` : INVALID_DATE_ERROR_MESSAGE
      case 'minDate':
      case 'minTime':
        return minDateTime ? `Please select a date after ${formatDateTimeWithLocale(minDateTime)}` : INVALID_DATE_ERROR_MESSAGE
      case 'invalidDate':
        return INVALID_DATE_ERROR_MESSAGE
      default:
        return required ? 'Required' : helperText
    }
  }, [internalError, helperText, required, maxDateTime, minDateTime])

  const CustomDay = useCallback(
    (pickersDayProps: PickersDayProps<Date>) => {
      const { day, ...other } = pickersDayProps
      const isDisabled = disableDate(day)

      return (
        <PickersDay
          {...other}
          day={day}
          sx={isDisabled ? { ...DAY_STYLES.disabled, ...DAY_STYLES.default } : DAY_STYLES.default}
        />
      )
    },
    [disableDate]
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
      <MuiDateTimePicker
        enableAccessibleFieldDOMStructure
        label={label}
        format={inputFormat}
        data-testid={dataTestId}
        value={value}
        onChange={handleChange}
        onError={handleError}
        maxDateTime={maxDateTime}
        minDateTime={minDateTime}
        disabled={disabled}
        minutesStep={minutesStep}
        shouldDisableDate={disableDate}
        slots={{ day: CustomDay }}
        slotProps={{
          actionBar: { actions: ['cancel', 'accept'] },
          textField: {
            id,
            fullWidth: true,
            helperText: computedHelperText,
            error: !!internalError || (touched && required && !isValid(value)),
            autoComplete: 'off',
            inputProps: { 'data-testid': dataTestId },
          },
        }}
      />
    </LocalizationProvider>
  )
}

export default DateTimePicker
